// import { useEffect, useState, useCallback } from "react";
// import axios from "axios";
// import API_BASE_URL from "../config";

// const useAccess = () => {
//   const [plan, setPlan] = useState(null);
//   const [loading, setLoading] = useState(true);

//   const fetchPlanStatus = useCallback(async () => {
//     try {
//       const res = await axios.get(`${API_BASE_URL}/plan/status`);
//       // Map backend response to logic: status 'active' means premium
//       setPlan(res.data.userPlan || null);
//     } catch (err) {
//       console.error("Access refetch failed:", err);
//       setPlan(null);
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     fetchPlanStatus();

//     // API Truth: Listen for global sync event
//     window.addEventListener('planUpdated', fetchPlanStatus);
//     return () => window.removeEventListener('planUpdated', fetchPlanStatus);
//   }, [fetchPlanStatus]);

//   const isPremium = plan?.status === "active";
//   const isAdmin = String(plan?.role) === '1';
//   const hasFullAccess = isPremium || isAdmin;

//   const triggerUpgrade = () => {
//     if (hasFullAccess) return;
//     window.dispatchEvent(new Event('goldpetals:open-upgrade'));
//   };

//   /**
//    * STRICT GLOBAL PAYWALL: Use this in click handlers.
//    * Returns true if allowed, false (and triggers modal) if restricted.
//    */
//   const checkAccess = () => {
//     if (!hasFullAccess) {
//       triggerUpgrade();
//       return false;
//     }
//     return true;
//   };

//   return { 
//     isPremium: hasFullAccess, 
//     isFree: !hasFullAccess, 
//     isAdmin,
//     userId: plan?.id, 
//     loading,
//     checkAccess, 
//     refreshAccess: fetchPlanStatus
//   };
// };

// export default useAccess;

import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../config";

// Module-level cache shared across all hook instances.
// Prevents multiple components from firing redundant /plan/status calls.
let planCache = null;
let isFetching = false;
const planListeners = new Set();

const notifyListeners = (plan) => {
  planListeners.forEach(fn => fn(plan));
};

const useAccess = () => {
  const [plan, setPlan] = useState(planCache); // use cache immediately if available
  const [loading, setLoading] = useState(!planCache); // skip loading if cached

  const fetchPlanStatus = useCallback(async (bustCache = false) => {
    // If another instance is already fetching, wait for it via listener
    if (isFetching && !bustCache) return;

    // Return cached value unless explicitly busting
    if (planCache && !bustCache) {
      setPlan(planCache);
      setLoading(false);
      return;
    }

    isFetching = true;
    setLoading(true);

    try {
      // ✅ withCredentials is critical for cookie-based sessions in production
      const res = await axios.get(`${API_BASE_URL}/plan/status`, {
        withCredentials: true
      });

      const userPlan = res.data?.userPlan || null;

      // Update module-level cache so other hook instances get it instantly
      planCache = userPlan;

      setPlan(userPlan);
      notifyListeners(userPlan); // sync all other mounted hook instances
    } catch (err) {
      console.error("useAccess fetch failed:", err);

      // On 401 → user is not logged in, clear cache
      if (err.response?.status === 401) {
        planCache = null;
        setPlan(null);
        notifyListeners(null);
      }
      // On network error → keep existing plan to avoid flicker
    } finally {
      isFetching = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Register this instance as a listener so cache updates propagate here
    const syncFromCache = (updatedPlan) => {
      setPlan(updatedPlan);
      setLoading(false);
    };
    planListeners.add(syncFromCache);

    // Fetch only if no cache exists yet
    fetchPlanStatus();

    // Listen for post-upgrade sync event from anywhere in the app
    // Usage: window.dispatchEvent(new Event('planUpdated'))
    const handlePlanUpdated = () => fetchPlanStatus(true); // bust cache on upgrade
    window.addEventListener('planUpdated', handlePlanUpdated);

    return () => {
      planListeners.delete(syncFromCache);
      window.removeEventListener('planUpdated', handlePlanUpdated);
    };
  }, [fetchPlanStatus]);

  // ── Derived state ──────────────────────────────────────────
  const isPremiumPlan = plan?.status === 'active';
  const isAdmin = String(plan?.role) === '1';
  const hasFullAccess = isPremiumPlan || isAdmin;

  // ── Actions ───────────────────────────────────────────────

  const triggerUpgrade = () => {
    if (hasFullAccess) return;
    window.dispatchEvent(new CustomEvent('goldpetals:open-upgrade', {
      detail: { feature: 'Full Access' }
    }));
  };

  /**
   * Use in click handlers for gated features.
   * Returns true if user can proceed, false if blocked (and opens modal).
   * 
   * Usage:
   *   const { checkAccess } = useAccess();
   *   const handleClick = () => {
   *     if (!checkAccess()) return;
   *     // proceed with premium action
   *   };
   */
  const checkAccess = useCallback((featureName = 'Full Access') => {
    if (hasFullAccess) return true;
    window.dispatchEvent(new CustomEvent('goldpetals:open-upgrade', {
      detail: { feature: featureName }
    }));
    return false;
  }, [hasFullAccess]);

  /**
   * Call after successful upgrade to bust cache and re-fetch from server.
   * This causes ALL components using useAccess to update simultaneously.
   * 
   * Usage:
   *   const { refreshAccess } = useAccess();
   *   await paymentApi();
   *   refreshAccess(); // all lock icons disappear instantly
   */
  const refreshAccess = useCallback(() => {
    fetchPlanStatus(true); // true = bust cache
  }, [fetchPlanStatus]);

  return {
    isPremium: hasFullAccess,  // true for both premium users AND admins
    isFree: !hasFullAccess,    // true only for free users
    isAdmin,                   // true only for admins
    userId: plan?.id,
    loading,
    checkAccess,
    refreshAccess,
  };
};

export default useAccess;